import type { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import {
    ColorNordicGrey1,
    ColorNordicGrey2,
    ColorNordicGrey5,
    ColorSystemAttention2,
    ColorSystemNegative2,
    Spacing8,
    Spacing16,
    Spacing24
} from 'autogen/design-tokens/tokens';
import HighlightedText from 'components/generic/HighlightedText';
import { Typography } from 'components/generic/typography';
import { handleSizesMediaQueryStyling } from 'helpers/mediaQueryStyling';
import format from 'helpers/translations';
import { type BreakPointsType, breakpoint, sizes } from 'variables';

type ActionFooterSizesKeys = 'md' | 'lg';
type ActionFooterSize = ActionFooterSizesKeys | BreakPointsType<ActionFooterSizesKeys>;

const sizeStyling = {
    md: css`
        padding: ${Spacing16} ${Spacing16} ${Spacing24};

        ${breakpoint.up(sizes.md)} {
            padding: ${Spacing16};
        }
    `,
    lg: css`
        padding: ${Spacing24};
    `
};

function getColor(messageType) {
    switch (messageType) {
        case 'warning':
            return 'ColorSystemAttention2';
        case 'error':
            return 'ColorSystemNegative2';
        default:
            return 'ColorSystemPositive1';
    }
}

export type ActionFooterProps = {
    ctaText?: string;
    onClick?: () => void;
    size?: ActionFooterSize;
    message?: string;
    messageType?: 'warning' | 'error';
};

const Wrapper = styled.div<{ $size: ActionFooterProps['size'] }>`
    display: flex;
    flex: none;
    flex-direction: column;
    gap: ${Spacing8};
    width: 100%;
    padding: ${Spacing16};
    background-color: ${ColorNordicGrey5};
    border-top: 1px solid ${ColorNordicGrey2};

    ${({ $size }) =>
        $size && (typeof $size === 'object' ? handleSizesMediaQueryStyling($size, sizeStyling) : sizeStyling[$size])}
`;

export default function ActionFooter(props: ActionFooterProps & HTMLAttributes<HTMLDivElement>) {
    const { size = 'md', message, messageType, children, ...restProps } = props;

    return (
        <Wrapper $size={size} {...restProps}>
            {message && (
                <HighlightedText color={getColor(messageType)} withBorder={false}>
                    <Typography.Body size="S" as="span">
                        {format(message)}
                    </Typography.Body>
                </HighlightedText>
            )}
            {children}
        </Wrapper>
    );
}
